<template>
  <parts-page-content v-if="pageData && isPreview" :page-data="pageData" :is-preview="isPreview"/>
  <div v-else-if="pageData" class="home-page">
    <main v-if="pageData" class="home-page__content">
      <article class="home-page__article">
        <page-breadcrumbs
          v-if="pageData?.databaseId"
          :post-id="pageData.databaseId"
          is-overlay
          is-inverted />
        <vue-enabled-html v-if="pageData" :content="pageData.formattedContent" />
      </article>
    </main>
  </div>
  <parts-product-page-content v-else-if="productData" :page-data="productData" :is-preview="isPreview" />
</template>

<script async setup lang="ts">
import { loadHeadMeta } from '~/composables/headmeta';
import { getProductById } from '~/hooks/queries/use-product-data-query.js';
import { fetchPostPreviewById } from '~/hooks/queries/use-page-data-query.js';
import {
  defineAsyncComponent,
  getPageContentFromUri,
  navigateTo,
  onBeforeUnmount,
  onMounted,
  ref,
  useNuxtApp,
  useRoute
} from '#imports';

const VueEnabledHtml = defineAsyncComponent(() => import('~/components/VueEnabledHtml.vue'));

const isLoading = ref(true);
const pageData = ref(null);
const productData = ref(null);
const route = useRoute()
const app = useNuxtApp()
const isPreview = ref(false);

const getProductPreview = async (productId, isPreview = false) => {
  try {
    getProductById(parseInt(productId, 10)).then(({ product }) => {
      if (product) {
        productData.value = product
        app.runWithContext(() => {
          loadHeadMeta(product)
        })
      } else {
        isPreview = false;
        navigateTo('/')
      }
    })
  } catch (err) {
    console.error(err)
    isPreview.value = false;
    navigateTo('/')
  }
}

const getPostPreview = async (postId, isPreview = false) => {
  try {
    fetchPostPreviewById({ postId: parseInt(postId, 10), isPreview: true }).then(({ postPreview }) => {
      if (postPreview) {
        pageData.value = postPreview
      } else {
        isPreview = false;
        navigateTo('/')
      }
    })
  } catch (err) {
    console.error(err)
    isPreview = false;
    navigateTo('/')
  }
}

// If preview - prevent SSR fetch and wait to fetch preview in client
if (route.query['preview']) {
  isPreview.value = !! route.query['preview'];
}

const getPageContent = async () => {
  const { data } = await getPageContentFromUri(route.path);

  isLoading.value = false;
  pageData.value = data;
}

const playTrack = (data) => {
  console.debug('Play audio id: ', data.detail.id);
}

const addToCart = async (eventData) => {
  await this.cartStore.addToCart(eventData.detail.id,  1);
}

const goToRelative = (eventData) => {
  navigateTo(eventData.detail.relative_permalink);
}

onMounted(async () => {
  window.addEventListener('play-track', playTrack);
  window.addEventListener('add-to-cart', addToCart);
  window.addEventListener('goto-relative', goToRelative);

  // fetch preview data in client
  if (isPreview.value) {

    if (route.query['post_type'] == 'product' && route.query['p']) {
      await getProductPreview(route.query['p'], true);
    } else if (route.query['page_id'] || route.query['p']) {
      const postId = route.query['page_id'] || route.query['p'];
      await getPostPreview(postId, true);
    }

    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('play-track', playTrack);
  window.removeEventListener('add-to-cart', addToCart);
  window.removeEventListener('goto-relative', goToRelative);
});

if (! isPreview.value) {
  await getPageContent();
}

</script>

<style lang="scss">
@use "~/common/scss/helpers";
@use "~/common/scss/mq";
@use "~/common/scss/container";
@use "~/common/scss/variables";

.home-page {
  --container-width: 95%;
  --container-max: var(--container-width);
  --item-slider-spacing: #{helpers.px-to-rem(60)};

  min-height: 95vh;

  @include mq.aboveTablet() {
    --container-width: 86.5%;
  }

  // evey element that is not the stage should not grow bigger than this
  .content > *:not(.home-stage):not(p) {
    margin-left: auto;
    margin-right: auto;
    max-width: var(--container-width);
  }

  .content p {
    @include container.paragraph();

    margin-bottom: 1rem;
  }

  .home-stage p {
    max-width: 100%;
    width: 100%;
  }

  &__article {
    position: relative;
  }
}
</style>
